import React from "react";
import { cn } from "../../../lib/utils";
import * as Checkbox from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { Label } from "../../ui/label";

const PrintLocationsSelectionForm = ({
  printLocationsList,
  selectedPrintLocations, // from formik.
  setSelectedPrintLocations,
}: any) => {
  const handleOnSelectionChange = (checked: boolean, slug: string) => {
    const currentSelectedPrintLocations = [...selectedPrintLocations];

    if (checked) {
      if (currentSelectedPrintLocations.length === 0) {
        currentSelectedPrintLocations.push({
          location: slug,
          default: true,
        });
      } else {
        currentSelectedPrintLocations.push({
          location: slug,
          default: false,
        });
      }
    } else {
      const index = currentSelectedPrintLocations.findIndex(
        (selectedPrintLocation: any) => selectedPrintLocation.location === slug
      );

      if (index !== -1) {
        // if this was the default location, then set the first location as default.
        if (currentSelectedPrintLocations[index].default) {
          currentSelectedPrintLocations.splice(index, 1);
          if (currentSelectedPrintLocations.length > 0)
            currentSelectedPrintLocations[0].default = true;
        } else {
          currentSelectedPrintLocations.splice(index, 1);
        }
      }
    }

    setSelectedPrintLocations(currentSelectedPrintLocations);
  };

  const handleDefaultLocationChange = (slug: string) => {
    const currentSelectedPrintLocations = [...selectedPrintLocations];

    if (currentSelectedPrintLocations.length === 0) {
      return;
    }

    const index = currentSelectedPrintLocations.findIndex(
      (selectedPrintLocation: any) => selectedPrintLocation.location === slug
    );

    if (index !== -1) {
      currentSelectedPrintLocations.forEach(
        (selectedPrintLocation: any, i: number) => {
          if (i === index) {
            selectedPrintLocation.default = true;
          } else {
            selectedPrintLocation.default = false;
          }
        }
      );

      setSelectedPrintLocations(currentSelectedPrintLocations);
    }
  };

  return (
    <>
      <Label className="block text-body mb-2 text-sm px-1">
        Print Locations
      </Label>

      <ScrollArea.Root className="w-full relative h-[200px] overflow-hidden">
        <ScrollArea.Viewport className="w-full h-full py-2 px-2 relative rounded bg-borderColor">
          <div className="/text-xs w-full flex items-end justify-between">
            <div className="w-full">Select Location</div>
            <div className="w-full">Default</div>
          </div>

          <div className="grid grid-cols-2">
            <div>
              {printLocationsList.map((printLocation: any, index: number) => {
                const isSelected = selectedPrintLocations?.some(
                  (selectedPrintLocation: any) =>
                    selectedPrintLocation.location === printLocation.slug
                );

                return (
                  <div
                    key={printLocation._id}
                    className={cn("flex items-center my-2")}
                  >
                    <Checkbox.Root
                      className="bg-black w-[25px] h-[25px] rounded hover:text-primary focus:ring outline-none"
                      checked={isSelected}
                      onCheckedChange={(checked) =>
                        handleOnSelectionChange(
                          checked as boolean,
                          printLocation.slug
                        )
                      }
                    >
                      <Checkbox.Indicator className="text-primary">
                        <CheckIcon />
                      </Checkbox.Indicator>
                    </Checkbox.Root>

                    <label
                      title={printLocation.internalName}
                      htmlFor={printLocation._id}
                      className="ml-2"
                    >
                      {printLocation.slug}
                    </label>
                  </div>
                );
              })}
            </div>

            <div className="flex items-end justify-start">
              <RadioGroup.Root
                value={
                  selectedPrintLocations?.find(
                    (selectedPrintLocation: any) =>
                      selectedPrintLocation.default
                  )?.location || null
                }
                onValueChange={(value) => handleDefaultLocationChange(value)}
              >
                {printLocationsList.map((printLocation: any, index: number) => {
                  return (
                    <div
                      key={printLocation._id}
                      className="flex w-full items-center justify-end my-2"
                    >
                      <RadioGroup.Item
                        key={printLocation._id}
                        className="size-[25px] cursor-default rounded-full bg-black outline-none hover:bg-blackLight hover:cursor-pointer"
                        value={printLocation.slug}
                        id={printLocation.slug}
                      >
                        <RadioGroup.Indicator className="relative flex size-full items-center justify-center after:block after:size-[11px] after:rounded-full after:bg-primary" />
                      </RadioGroup.Item>
                    </div>
                  );
                })}
              </RadioGroup.Root>
            </div>
          </div>
        </ScrollArea.Viewport>

        {/* Ensure scrollbar stays inside the scrollable box */}
        <ScrollArea.Scrollbar
          className="flex select-none touch-none p-0.5 transition-colors duration-[160ms] ease-out hover:bg-black data-[orientation=vertical]:w-2.5"
          orientation="vertical"
        >
          <ScrollArea.Thumb className="flex-1 bg-scrollbarColor rounded-[10px]" />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>
    </>
  );
};

export default PrintLocationsSelectionForm;
